// -------------- Hook to call the api endpoint for the google trends ---------------
// ----------------------------------------------------------------------------------
// import functions and service
import { useState, useEffect, useCallback } from 'react';
import { getGoogleTrendsData } from '../services/api';

// hook to fetch Google Trends data for a given timeframe
export const useGoogleTrends = (timeframe = '3M') => {

  // define state variables used in the hook
  const [data, setData] = useState([]);  // data for the chart
  const [rollingKeywords, setRollingKeywords] = useState([]); // keywords for rolling averages
  const [loading, setLoading] = useState(true); // is the data being collected?
  const [error, setError] = useState(null);

  const fetchTrends = useCallback(async () => { // function to fetch data from API
    try {
      setLoading(true); // set the loading state
      setError(null); // reset any past errors

      // build the parameters for the request
      const params = { timeframe };
      const response = await getGoogleTrendsData(params); // make the API request
      
      // make sure the request happen as it should've
      if (!response?.dates || !response?.trends) {
        throw new Error('Invalid trends data received'); // if any errors are found, throw an error
      }

      // process the response data
      const { dates, trends } = response;
      const allKeys = Object.keys(trends); // get all the keys from the trends object
      const rollingKeys = allKeys.filter(k => k.endsWith('_rolling') || k.includes('rolling'));
      setRollingKeywords(rollingKeys.length > 0 ? rollingKeys : allKeys); // set the rolling keywords state

      // transform the data into a format suitable for charting
      const chartData = dates.map((date, index) => {

        const entry = { date }; // create an entry for each date

        // loop through each keyword and add its value to the entry
        Object.keys(trends).forEach(keyword => {
          entry[keyword] = trends[keyword][index] || 0;
        });

        return entry; // creates a .json with each date as a row
      });

      setData(chartData);

    } catch (err) { // catch any errors that occur during the fetch
      console.error('Error fetching trends data:', err);
      setError(err.message || 'Failed to fetch trends data');
      setData([]);
      setRollingKeywords([]);

    } finally {
      setLoading(false); // set loading to false as collecting has finished
    }
  }, [timeframe]);

  useEffect(() => { // call the fetchTrends function when the component mounts or when the timeframe changes
    fetchTrends();
  }, [fetchTrends]);

  return { 
    data, 
    rollingKeywords, 
    loading, 
    error, 
    refetch: fetchTrends 
  }; // return the data and refetch function to allow manual refresh of the data
};
